import React from 'react'
import {
    StoredIndividualization,
    StoredSpot,
    StoredTemplate,
} from '../../../../types/Individualization'
import ToggleEye from '../../../../components/ToggleEye'
import IconButton from '../../../../components/IconButton'
import { faCopy, faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import Carousel from '../../../../components/Carousel'
import Heading from '../../../../components/Heading'
import Card from '../../../../components/Card'
import cx from 'classnames'
import styles from './IndividualizationCard.module.css'
import { useTranslation } from 'react-i18next'
import Text from '../../../../components/Text'

type IndividualizationCardProps = {
    individualization: StoredIndividualization
    toggleVisibility: (individualization_id: number) => void
    deleteIndividualization: (individualization_id: number) => void
    editIndividualization: (individualization_id: number) => void
    copyIndividualization: (individualization_id: number) => void
    demoIndividualization: (individualization_id: number) => void
}

const IndividualizationCard: React.FC<IndividualizationCardProps> = ({
    individualization,
    toggleVisibility,
    deleteIndividualization,
    editIndividualization,
    copyIndividualization,
    demoIndividualization,
}) => {
    const { t } = useTranslation(['individualization'])

    const getSurchargePrice = (
        element: StoredIndividualization | StoredTemplate | StoredSpot
    ): JSX.Element => {
        if (element.surcharge_price)
            return (
                <span
                    dangerouslySetInnerHTML={{
                        __html: element.surcharge_price[0],
                    }}
                />
            )
        else return <span>0,00€</span>
    }
    return (
        <Card key={individualization.id}>
            <div className={cx(styles['card-buttons'])}>
                {individualization.article ? (
                    <IconButton
                        icon={faEdit}
                        color={'#2f2f2f'}
                        title={t('overview.edit')}
                        onClick={() =>
                            editIndividualization(individualization.id)
                        }
                    />
                ) : null}
                <IconButton
                    icon={faCopy}
                    color={'#2f2f2f'}
                    title={t('overview.copy')}
                    onClick={() => copyIndividualization(individualization.id)}
                />
                <ToggleEye
                    title={
                        individualization.active
                            ? t('overview.visibilityActive')
                            : t('overview.visibilityInactive')
                    }
                    toggleEye={() => toggleVisibility(individualization.id)}
                    status={individualization.active}
                    border={false}
                />
                <IconButton
                    icon={faTrashAlt}
                    color={'#2f2f2f'}
                    title={t('overview.delete')}
                    onClick={() =>
                        deleteIndividualization(individualization.id)
                    }
                />
            </div>
            <div
                className={
                    'zr-w-100 zr-d-flex zr-justify-between zr-items-start'
                }
            >
                {individualization.article ? (
                    <a
                        href={individualization?.article?.url}
                        target={'_blank'}
                        className={cx(styles['card-headings'])}
                        dangerouslySetInnerHTML={{
                            __html: individualization?.article?.cName,
                        }}
                        rel="noreferrer"
                    />
                ) : (
                    <div className={cx(styles['missing-article-hint'])}>
                        {t('overview.articleMissing', {
                            articleId: individualization.kArtikel,
                        })}
                    </div>
                )}
            </div>
            <div>
                {individualization.article.variations?.map((variation) => {
                    return individualization.variations?.[
                        variation.kEigenschaft
                    ] ? (
                        <Text key={variation.kEigenschaft}>
                            <small>
                                <b>{variation.cName}</b>:{' '}
                                {individualization.variations?.[
                                    variation.kEigenschaft
                                ]
                                    ?.map((value) => {
                                        return variation.values.find(
                                            (val) =>
                                                val.kEigenschaftWert === value
                                        )?.cName
                                    })
                                    .join(', ')}
                            </small>
                        </Text>
                    ) : null
                })}
            </div>
            {individualization.customer_groups_labels &&
                <div>
                    <Text>
                        <small>
                            <b>{t('overview.customer_group')}: </b>
                            {individualization.customer_groups_labels?.map((value) => {
                                return value
                            }).join(', ')}

                        </small>
                    </Text>
                </div>
            }
            <p className={'zr-mt-0 zr-my-2'}>
                Globaler Aufpreis {getSurchargePrice(individualization)}
            </p>
            <Carousel
                classes={'zr-mb-6'}
                time={5000}
                items={individualization.templates.map(
                    (template: StoredTemplate) => {
                        return (
                            <div
                                className={cx(styles['card-carousel-item'])}
                                key={template.id}
                            >
                                <img
                                    src={template.src}
                                    alt={template.name}
                                    className={cx(styles['carousel-item-img'])}
                                />
                                <div
                                    className={cx(styles['carousel-item-list'])}
                                >
                                    <div
                                        className={cx(
                                            styles['carousel-item-list-ele']
                                        )}
                                    >
                                        <Heading level={4}>
                                            {template.name}
                                        </Heading>
                                        {getSurchargePrice(template)}
                                    </div>
                                    {template.spots.map((spot: StoredSpot) => {
                                        return (
                                            <div
                                                className={cx(
                                                    styles[
                                                        'carousel-item-list-ele'
                                                    ]
                                                )}
                                                key={spot.id}
                                            >
                                                <span key={spot.id}>
                                                    {spot.name}
                                                </span>
                                                {getSurchargePrice(spot)}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )
                    }
                )}
            />
            {individualization.article && (
                <p
                    onClick={() => demoIndividualization(individualization.id)}
                    className={cx(styles['card-demo'])}
                >
                    {t('overview.demo')}
                </p>
            )}
        </Card>
    )
}

export default IndividualizationCard
